import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TablePagination,
  Stack,
  IconButton,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  Link,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CasinoIcon from '@mui/icons-material/Casino'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ClearIcon from '@mui/icons-material/Clear'
import BlockIcon from '@mui/icons-material/Block'
import ReplayIcon from '@mui/icons-material/Replay'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditIcon from '@mui/icons-material/Edit'
import { useNavigate } from 'react-router-dom'
import {
  shopService,
  appUserService,
  appUserWhitelistService,
} from '../services'
import useAuthStore from '../store/authStore'
import CreateShopWizard from '../components/shops/CreateShopWizard'
import CheckBalanceButton from '../components/CheckBalanceButton'
import ShopAppLockSummary from '../components/shops/ShopAppLockSummary'
import ShopDocumentUploadField from '../components/shops/ShopDocumentUploadField'
import {
  formatCustomerCodeInput,
  isValidCustomerCode,
} from '../utils/customerCode'
import { formatDate, formatDateTime } from '../utils/dateFormat'
import {
  hasAdminPermission,
  PERMISSION_SUBJECTS,
  canRegenerateShopAppLockPin,
} from '../utils/adminPermissions'

const CLOSE_REASONS = [
  { value: 'BUSINESS_CLOSURE', label: 'Business Closure' },
  { value: 'RELOCATION', label: 'Relocation' },
  { value: 'OWNERSHIP_CHANGE', label: 'Ownership Change' },
  { value: 'PARTNERSHIP_ENDED', label: 'Partnership Ended' },
  { value: 'OTHER', label: 'Others' },
]

const TAB_STATUS = ['active', 'pending', 'rejected', 'closed']

const ShopsPage = () => {
  const navigate = useNavigate()
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const adminRoleCodes = useAuthStore((s) => s.adminRoleCodes)
  const canRegenerateAppLockPin = canRegenerateShopAppLockPin(
    isSuperuserActor,
    adminRoleCodes,
  )
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canCreateShop = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.APP_USER_WHITELIST,
    'write',
  )

  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [error, setError] = useState(null)
  const [snackbar, setSnackbar] = useState(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [addPhoneOpen, setAddPhoneOpen] = useState(false)
  const [addPhone, setAddPhone] = useState('')
  const [addPhoneSubmitting, setAddPhoneSubmitting] = useState(false)

  const [approveTarget, setApproveTarget] = useState(null)
  const [approveForm, setApproveForm] = useState({
    customer_code: '',
    shop_owner_name: '',
    shop_name: '',
    address: '',
  })
  const [approveSubmitting, setApproveSubmitting] = useState(false)

  const [rejectTarget, setRejectTarget] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectSubmitting, setRejectSubmitting] = useState(false)

  const [closeTarget, setCloseTarget] = useState(null)
  const [closeReason, setCloseReason] = useState('BUSINESS_CLOSURE')
  const [closeDetail, setCloseDetail] = useState('')
  const [closeSubmitting, setCloseSubmitting] = useState(false)

  const [reopenTarget, setReopenTarget] = useState(null)
  const [reopenReason, setReopenReason] = useState('')
  const [reopenSubmitting, setReopenSubmitting] = useState(false)

  const [viewTarget, setViewTarget] = useState(null)
  const [viewData, setViewData] = useState(null)
  const [viewLoading, setViewLoading] = useState(false)
  const [rejectedViewTarget, setRejectedViewTarget] = useState(null)

  const [editTarget, setEditTarget] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [editSubmitting, setEditSubmitting] = useState(false)

  const [exportAnchor, setExportAnchor] = useState(null)
  const [exporting, setExporting] = useState(false)

  const [generatedPin, setGeneratedPin] = useState(null)
  const [generatedPinUser, setGeneratedPinUser] = useState(null)
  const [generating, setGenerating] = useState(false)

  const [appLockRegenOpen, setAppLockRegenOpen] = useState(false)
  const [appLockRegenSubmitting, setAppLockRegenSubmitting] = useState(false)
  const [appLockRegenResult, setAppLockRegenResult] = useState(null)

  const status = TAB_STATUS[tab]

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await shopService.list({
        status,
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
      })
      setRows(data.items || [])
      setTotal(data.pagination?.total || 0)
      setError(null)
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load shops')
    } finally {
      setLoading(false)
    }
  }, [status, page, rowsPerPage, search])

  useEffect(() => {
    load()
  }, [load])

  const handleTabChange = (_, newTab) => {
    setTab(newTab)
    setPage(0)
  }

  const handleGeneratePin = async (row) => {
    if (!row.user_id) return
    setGenerating(true)
    try {
      const result = await appUserService.generatePin(row.user_id)
      setGeneratedPin(result.pin)
      setGeneratedPinUser(row)
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message || e.message || 'Failed to generate PIN',
      })
    } finally {
      setGenerating(false)
    }
  }

  const openApproveDialog = (row) => {
    setApproveTarget(row)
    setApproveForm({
      customer_code: formatCustomerCodeInput(
        row.customer_code ?? row.org_id ?? '',
      ),
      shop_owner_name: row.owner_name || '',
      shop_name: row.shop_name || '',
      address: row.address || '',
    })
  }

  const closeApproveDialog = () => {
    setApproveTarget(null)
    setApproveForm({
      customer_code: '',
      shop_owner_name: '',
      shop_name: '',
      address: '',
    })
  }

  const approveFormValid =
    isValidCustomerCode(approveForm.customer_code) &&
    approveForm.shop_owner_name.trim() &&
    approveForm.shop_name.trim() &&
    approveForm.address.trim()

  const handleApprove = async () => {
    if (!approveTarget?.request_id || !approveFormValid) return
    setApproveSubmitting(true)
    try {
      await shopService.approveRequest(approveTarget.request_id, {
        customer_code: formatCustomerCodeInput(approveForm.customer_code),
        shop_owner_name: approveForm.shop_owner_name.trim(),
        shop_name: approveForm.shop_name.trim(),
        address: approveForm.address.trim(),
      })
      setSnackbar({ type: 'success', message: 'Account request approved' })
      closeApproveDialog()
      await load()
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message || e.message || 'Failed to approve request',
      })
    } finally {
      setApproveSubmitting(false)
    }
  }

  const closeRejectDialog = () => {
    setRejectTarget(null)
    setRejectReason('')
  }

  const handleReject = async () => {
    if (!rejectTarget?.request_id) return
    setRejectSubmitting(true)
    try {
      await shopService.rejectRequest(
        rejectTarget.request_id,
        rejectReason.trim() || undefined,
      )
      setSnackbar({ type: 'success', message: 'Account request rejected' })
      closeRejectDialog()
      await load()
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message || e.message || 'Failed to reject request',
      })
    } finally {
      setRejectSubmitting(false)
    }
  }

  const handleCloseShop = async () => {
    if (!closeTarget?.org_id) return
    setCloseSubmitting(true)
    try {
      await shopService.closeShop(closeTarget.org_id, {
        reason: closeReason,
        ...(closeReason === 'OTHER' ? { reason_detail: closeDetail } : {}),
      })
      setSnackbar({ type: 'success', message: 'Shop closed' })
      setCloseTarget(null)
      setCloseDetail('')
      await load()
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message || e.message || 'Failed to close shop',
      })
    } finally {
      setCloseSubmitting(false)
    }
  }

  const handleReopen = async () => {
    if (!reopenTarget?.org_id) return
    setReopenSubmitting(true)
    try {
      await shopService.reopenShop(reopenTarget.org_id, {
        reason: reopenReason.trim(),
      })
      setSnackbar({ type: 'success', message: 'Shop reopened' })
      setReopenTarget(null)
      setReopenReason('')
      await load()
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message || e.message || 'Failed to reopen shop',
      })
    } finally {
      setReopenSubmitting(false)
    }
  }

  const handleView = async (row) => {
    if (!row.org_id) return
    setViewTarget(row)
    setViewLoading(true)
    try {
      const data = await shopService.get(row.org_id)
      setViewData(data)
    } catch (e) {
      setSnackbar({
        type: 'error',
        message: e.response?.data?.message || e.message || 'Failed to load shop',
      })
      setViewTarget(null)
    } finally {
      setViewLoading(false)
    }
  }

  const handleRegenerateAppLockPin = async () => {
    if (!viewData?.org_id) return
    setAppLockRegenSubmitting(true)
    try {
      const result = await shopService.regenerateAppLockPin(viewData.org_id)
      setAppLockRegenResult(result)
      setAppLockRegenOpen(false)
      const refreshed = await shopService.get(viewData.org_id)
      setViewData(refreshed)
      setSnackbar({
        type: 'success',
        message: 'App Lock PIN regenerated',
      })
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message ||
          e.message ||
          'Failed to regenerate App Lock PIN',
      })
    } finally {
      setAppLockRegenSubmitting(false)
    }
  }

  const handleOpenEdit = async (row) => {
    if (!row.org_id) return
    setEditTarget(row)
    try {
      const data = await shopService.get(row.org_id)
      setEditForm({
        shop_owner_name: data.owner_name || '',
        shop_name: data.shop_name || '',
        address: data.address || '',
        email: data.email || '',
        geolocation:
          typeof data.geolocation === 'string'
            ? data.geolocation.replace(/[()]/g, '').replace(',', ', ')
            : '',
        trade_license_url: data.trade_license_url || '',
        nid_url: data.nid_url || '',
        shop_photo_url: data.shop_photo_url || '',
        // TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
        // Loaded from GET /admin/shops/:id; remove field + Switch when all shops are integrated.
        shipment_external_integration_enabled:
          data.shipment_external_integration_enabled === true,
      })
    } catch (e) {
      setSnackbar({
        type: 'error',
        message: e.response?.data?.message || e.message || 'Failed to load shop',
      })
      setEditTarget(null)
    }
  }

  const handleSaveEdit = async () => {
    if (!editTarget?.org_id) return
    setEditSubmitting(true)
    try {
      const trimToNull = (value) => {
        const trimmed = (value ?? '').trim()
        return trimmed === '' ? null : trimmed
      }
      const payload = {
        shop_owner_name: trimToNull(editForm.shop_owner_name),
        shop_name: trimToNull(editForm.shop_name),
        address: trimToNull(editForm.address),
        email: trimToNull(editForm.email),
        geolocation: trimToNull(editForm.geolocation),
        trade_license_url: trimToNull(editForm.trade_license_url),
        nid_url: trimToNull(editForm.nid_url),
        shop_photo_url: trimToNull(editForm.shop_photo_url),
        // TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
        shipment_external_integration_enabled:
          editForm.shipment_external_integration_enabled === true,
      }
      await shopService.update(editTarget.org_id, payload)
      setSnackbar({ type: 'success', message: 'Shop updated' })
      setEditTarget(null)
      await load()
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message || e.message || 'Failed to update shop',
      })
    } finally {
      setEditSubmitting(false)
    }
  }

  const handleToggleWhitelist = async (row) => {
    if (!row.whitelist_id) return
    try {
      await appUserWhitelistService.setActive(
        row.whitelist_id,
        !row.whitelist_active,
      )
      setSnackbar({ type: 'success', message: 'Whitelist updated' })
      await load()
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message || e.message || 'Failed to update whitelist',
      })
    }
  }

  const handleDeleteWhitelist = async (row) => {
    if (!row.whitelist_id) return
    try {
      await appUserWhitelistService.remove(row.whitelist_id)
      setSnackbar({ type: 'success', message: 'Removed from whitelist' })
      await load()
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message || e.message || 'Failed to remove entry',
      })
    }
  }

  const handleAddPhone = async () => {
    let local = (addPhone || '').replace(/\D/g, '')
    if (local.length === 11 && local.startsWith('0')) {
      local = local.slice(1)
    }
    if (local.length !== 10) {
      setSnackbar({
        type: 'error',
        message: 'Phone number must be 10 digits (or 11 if it starts with 0)',
      })
      return
    }
    const e164 = `+880${local}`
    setAddPhoneSubmitting(true)
    try {
      await appUserWhitelistService.add(e164)
      setSnackbar({ type: 'success', message: 'Phone number added to whitelist' })
      setAddPhoneOpen(false)
      setAddPhone('')
      await load()
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message || e.message || 'Failed to add phone number',
      })
    } finally {
      setAddPhoneSubmitting(false)
    }
  }

  const handleExport = async (format) => {
    setExportAnchor(null)
    setExporting(true)
    try {
      const response = await shopService.createExport({
        format,
        status,
        search: search.trim() || undefined,
      })
      navigate('/shops/exports', {
        state: {
          queuedMessage:
            response?.message ||
            'Export started. It will appear in Shop Exports when the file is ready.',
        },
      })
    } catch (e) {
      setSnackbar({
        type: 'error',
        message:
          e.response?.data?.message || e.message || 'Failed to start shop export',
      })
    } finally {
      setExporting(false)
    }
  }

  const statusChip = (row) => {
    const color =
      row.status === 'ACTIVE'
        ? 'success'
        : row.status === 'CLOSED'
          ? 'default'
          : row.status === 'REJECTED'
            ? 'error'
            : 'warning'
    return <Chip size="small" label={row.status} color={color} />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Shops
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/shops/exports')}
          sx={{ mr: 1 }}
        >
          Shop Exports
        </Button>
        <Button
          variant="outlined"
          startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={(event) => setExportAnchor(event.currentTarget)}
          disabled={exporting}
          sx={{ mr: 1 }}
        >
          Export
        </Button>
        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={() => setExportAnchor(null)}
        >
          <MenuItem onClick={() => handleExport('csv')}>CSV</MenuItem>
          <MenuItem onClick={() => handleExport('xlsx')}>Excel (.xlsx)</MenuItem>
        </Menu>
        {isSuperuserActor && (
          <Button
            variant="outlined"
            onClick={() => navigate('/administration/sales-policy')}
            sx={{ mr: 1 }}
          >
            Sales Policy
          </Button>
        )}
        {canCreateShop && status === 'active' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ mr: 1 }}
          >
            Create Shop
          </Button>
        )}
        {status === 'pending' && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddPhoneOpen(true)}
          >
            Add Phone Number
          </Button>
        )}
      </Box>

      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Active" />
        <Tab label="Pending" />
        <Tab label="Rejected" />
        <Tab label="Closed" />
      </Tabs>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search phone, shop name, customer code, or shop ID…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 320 }}
              InputProps={{
                endAdornment: search ? (
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => {
                      setSearch('')
                      setPage(0)
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer Code</TableCell>
                      <TableCell>Shop Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Joined Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No shops found.
                        </TableCell>
                      </TableRow>
                    )}
                    {rows.map((row) => (
                      <TableRow key={`${row.row_type}-${row.request_id || row.whitelist_id || row.org_id}`} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {row.org_id ?? '—'}
                        </TableCell>
                        <TableCell>{row.shop_name || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {row.phone || '—'}
                        </TableCell>
                        <TableCell>
                          {formatDate(row.joined_date)}
                        </TableCell>
                        <TableCell>{statusChip(row)}</TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                            flexWrap="wrap"
                            alignItems="flex-start"
                          >
                            {row.org_id ? (
                              <CheckBalanceButton orgId={row.org_id} compact />
                            ) : null}
                            {status === 'active' && row.user_id && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<CasinoIcon fontSize="small" />}
                                onClick={() => handleGeneratePin(row)}
                                disabled={generating}
                              >
                                Generate
                              </Button>
                            )}
                            {status === 'active' && row.org_id && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon fontSize="small" />}
                                  onClick={() => handleView(row)}
                                >
                                  View
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon fontSize="small" />}
                                  onClick={() => handleOpenEdit(row)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  startIcon={<BlockIcon fontSize="small" />}
                                  onClick={() => {
                                    setCloseTarget(row)
                                    setCloseReason('BUSINESS_CLOSURE')
                                    setCloseDetail('')
                                  }}
                                >
                                  Close
                                </Button>
                              </>
                            )}
                            {status === 'pending' &&
                              row.row_type === 'account_request' && (
                                <>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckIcon fontSize="small" />}
                                    onClick={() => openApproveDialog(row)}
                                  >
                                    Review Request
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<CloseIcon fontSize="small" />}
                                    onClick={() => {
                                      setRejectTarget(row)
                                      setRejectReason('')
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                            {status === 'pending' &&
                              row.row_type === 'whitelist_only' && (
                                <>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                  >
                                    <Typography variant="caption">
                                      Active
                                    </Typography>
                                    <Switch
                                      size="small"
                                      checked={!!row.whitelist_active}
                                      onChange={() => handleToggleWhitelist(row)}
                                    />
                                  </Stack>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteWhitelist(row)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            {status === 'rejected' &&
                              row.row_type === 'account_request' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon fontSize="small" />}
                                  onClick={() => setRejectedViewTarget(row)}
                                >
                                  View
                                </Button>
                              )}
                            {status === 'closed' && row.org_id && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon fontSize="small" />}
                                  onClick={() => handleView(row)}
                                >
                                  View
                                </Button>
                                {isSuperuserActor && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<ReplayIcon fontSize="small" />}
                                    onClick={() => setReopenTarget(row)}
                                  >
                                    Reopen
                                  </Button>
                                )}
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>

      <CreateShopWizard
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCompleted={() => {
          setSnackbar({ type: 'success', message: 'Shop created' })
          load()
        }}
      />

      <Dialog
        open={addPhoneOpen}
        onClose={() => !addPhoneSubmitting && setAddPhoneOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Add phone to whitelist</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Phone number"
            value={addPhone}
            onChange={(e) => setAddPhone(e.target.value)}
            helperText="10 digits (Bangladesh)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPhoneOpen(false)} disabled={addPhoneSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPhone}
            disabled={addPhoneSubmitting}
          >
            {addPhoneSubmitting ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!approveTarget}
        onClose={() => !approveSubmitting && closeApproveDialog()}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Approve account request</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2">
              <strong>Phone:</strong> {approveTarget?.phone || '—'}
            </Typography>
            <TextField
              label="Shop ID"
              value={approveForm.customer_code}
              onChange={(e) =>
                setApproveForm((f) => ({
                  ...f,
                  customer_code: formatCustomerCodeInput(e.target.value),
                }))
              }
              fullWidth
              required
              helperText="Exactly 10 digits starting with 9 (e.g. 9123456789)"
              inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            />
            <TextField
              label="Shop Owner Name"
              value={approveForm.shop_owner_name}
              onChange={(e) =>
                setApproveForm((f) => ({
                  ...f,
                  shop_owner_name: e.target.value,
                }))
              }
              fullWidth
              required
            />
            <TextField
              label="Shop Name"
              value={approveForm.shop_name}
              onChange={(e) =>
                setApproveForm((f) => ({ ...f, shop_name: e.target.value }))
              }
              fullWidth
              required
            />
            <TextField
              label="Address"
              value={approveForm.address}
              onChange={(e) =>
                setApproveForm((f) => ({ ...f, address: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApproveDialog} disabled={approveSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={approveSubmitting || !approveFormValid}
          >
            {approveSubmitting ? 'Approving…' : 'Approve account request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!rejectTarget}
        onClose={() => !rejectSubmitting && closeRejectDialog()}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject account request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Rejecting request for <strong>{rejectTarget?.phone || '—'}</strong>
            {rejectTarget?.shop_name ? (
              <>
                {' '}
                (<strong>{rejectTarget.shop_name}</strong>)
              </>
            ) : null}
            . This cannot be undone.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectDialog} disabled={rejectSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleReject}
            disabled={rejectSubmitting}
          >
            {rejectSubmitting ? 'Rejecting…' : 'Reject account request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!closeTarget}
        onClose={() => !closeSubmitting && setCloseTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Close shop</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Closing <strong>{closeTarget?.shop_name}</strong> (ID{' '}
            {closeTarget?.org_id}). The shop owner will no longer be able to log
            in.
          </Typography>
          <TextField
            select
            fullWidth
            label="Reason"
            value={closeReason}
            onChange={(e) => setCloseReason(e.target.value)}
            sx={{ mb: 2 }}
          >
            {CLOSE_REASONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          {closeReason === 'OTHER' && (
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Please describe the reason"
              value={closeDetail}
              onChange={(e) => setCloseDetail(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseTarget(null)} disabled={closeSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleCloseShop}
            disabled={
              closeSubmitting ||
              (closeReason === 'OTHER' && !closeDetail.trim())
            }
          >
            {closeSubmitting ? 'Closing…' : 'Close Shop'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!reopenTarget}
        onClose={() => !reopenSubmitting && setReopenTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reopen shop</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason for reopening (required)"
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenTarget(null)} disabled={reopenSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReopen}
            disabled={reopenSubmitting || !reopenReason.trim()}
          >
            {reopenSubmitting ? 'Reopening…' : 'Reopen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!viewTarget}
        onClose={() => {
          setViewTarget(null)
          setViewData(null)
          setAppLockRegenResult(null)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Shop details</DialogTitle>
        <DialogContent>
          {viewLoading ? (
            <CircularProgress size={28} />
          ) : viewData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Shop ID:</strong> {viewData.org_id}
              </Typography>
              <Box sx={{ my: 1 }}>
                <CheckBalanceButton orgId={viewData.org_id} />
              </Box>
              <ShopAppLockSummary
                appLock={viewData.app_lock}
                onRegeneratePin={
                  canRegenerateAppLockPin
                    ? () => setAppLockRegenOpen(true)
                    : undefined
                }
              />
              <Typography variant="body2">
                <strong>Shop Name:</strong> {viewData.shop_name}
              </Typography>
              <Typography variant="body2">
                <strong>Owner:</strong> {viewData.owner_name || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Phone:</strong> {viewData.phone || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {viewData.email || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Address:</strong> {viewData.address || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {viewData.status}
              </Typography>
              {/* TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT */}
              <Typography variant="body2">
                <strong>Shipment SAP/Aspen integration:</strong>{' '}
                {viewData.shipment_external_integration_enabled
                  ? 'Enabled'
                  : 'Disabled (local-only)'}
              </Typography>
              {viewData.trade_license_url && (
                <Typography variant="body2">
                  <strong>Trade License:</strong>{' '}
                  <Link
                    href={viewData.trade_license_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View file
                  </Link>
                </Typography>
              )}
              {viewData.nid_url && (
                <Typography variant="body2">
                  <strong>NID:</strong>{' '}
                  <Link
                    href={viewData.nid_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View file
                  </Link>
                </Typography>
              )}
              {viewData.shop_photo_url && (
                <Typography variant="body2">
                  <strong>Shop Photo:</strong>{' '}
                  <Link
                    href={viewData.shop_photo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View file
                  </Link>
                </Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewTarget(null)
              setViewData(null)
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!rejectedViewTarget}
        onClose={() => setRejectedViewTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Rejected account request</DialogTitle>
        <DialogContent>
          {rejectedViewTarget ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Shop Name:</strong>{' '}
                {rejectedViewTarget.shop_name || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Owner:</strong>{' '}
                {rejectedViewTarget.owner_name || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Phone:</strong> {rejectedViewTarget.phone || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Customer Code:</strong>{' '}
                {rejectedViewTarget.org_id ??
                  rejectedViewTarget.customer_code ??
                  '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Address:</strong>{' '}
                {rejectedViewTarget.address || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Requested:</strong>{' '}
                {formatDateTime(rejectedViewTarget.joined_date)}
              </Typography>
              <Typography variant="body2">
                <strong>Rejected:</strong>{' '}
                {formatDateTime(rejectedViewTarget.reviewed_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Reason:</strong>{' '}
                {rejectedViewTarget.rejection_reason || '—'}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectedViewTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editTarget}
        onClose={() => !editSubmitting && setEditTarget(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Edit shop</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Shop Owner Name (optional)"
              value={editForm.shop_owner_name || ''}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, shop_owner_name: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Shop Name (optional)"
              value={editForm.shop_name || ''}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, shop_name: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Address (optional)"
              value={editForm.address || ''}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, address: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Email (optional)"
              value={editForm.email || ''}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, email: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Geolocation (optional)"
              value={editForm.geolocation || ''}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, geolocation: e.target.value }))
              }
              fullWidth
              helperText="latitude,longitude"
            />
            {/*
              TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
              Toggle uses PATCH /admin/shops/:id (APP_USER_WHITELIST write).
              Remove Switch when every shop uses the integrated shipment flow.
            */}
            <FormControlLabel
              control={
                <Switch
                  checked={
                    editForm.shipment_external_integration_enabled === true
                  }
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      shipment_external_integration_enabled: e.target.checked,
                    }))
                  }
                  disabled={editSubmitting}
                />
              }
              label="Enable shipment SAP / Aspen integration"
            />
            <Typography variant="caption" color="text.secondary">
              When off, fulfillment-center assign is local-only (no SO create,
              record, DSD dialog, or Aspen receive-items). Takes effect on the
              next shipment create without redeploying.
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
              Shop documents (optional)
            </Typography>
            <ShopDocumentUploadField
              orgId={editTarget?.org_id}
              docType="trade_license"
              label="Trade License"
              currentUrl={editForm.trade_license_url}
              onUploaded={(url) =>
                setEditForm((f) => ({ ...f, trade_license_url: url }))
              }
            />
            <ShopDocumentUploadField
              orgId={editTarget?.org_id}
              docType="nid"
              label="NID"
              currentUrl={editForm.nid_url}
              onUploaded={(url) =>
                setEditForm((f) => ({ ...f, nid_url: url }))
              }
            />
            <ShopDocumentUploadField
              orgId={editTarget?.org_id}
              docType="shop_photo"
              label="Shop Photo"
              currentUrl={editForm.shop_photo_url}
              onUploaded={(url) =>
                setEditForm((f) => ({ ...f, shop_photo_url: url }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={editSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={editSubmitting}
          >
            {editSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!generatedPin}
        onClose={() => {
          setGeneratedPin(null)
          setGeneratedPinUser(null)
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Generated PIN</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This PIN is shown only once. Copy it now.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            For <strong>{generatedPinUser?.phone}</strong>:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#f5f5f5',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontFamily: 'monospace', letterSpacing: '0.4em', flexGrow: 1 }}
            >
              {generatedPin}
            </Typography>
            <Button
              onClick={async () => {
                await navigator.clipboard.writeText(generatedPin)
                setSnackbar({ type: 'success', message: 'PIN copied' })
              }}
              startIcon={<ContentCopyIcon />}
              size="small"
              variant="outlined"
            >
              Copy
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setGeneratedPin(null)
              setGeneratedPinUser(null)
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={appLockRegenOpen}
        onClose={() => !appLockRegenSubmitting && setAppLockRegenOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Regenerate App Lock PIN</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Generate a new 4-digit App Lock PIN for{' '}
            <strong>{viewData?.shop_name || 'this shop'}</strong>? The previous
            PIN will stop working after devices sync.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAppLockRegenOpen(false)}
            disabled={appLockRegenSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRegenerateAppLockPin}
            disabled={appLockRegenSubmitting}
          >
            {appLockRegenSubmitting ? 'Regenerating…' : 'Regenerate PIN'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!appLockRegenResult?.pin}
        onClose={() => setAppLockRegenResult(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>App Lock PIN</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Share this PIN with the shop owner. It is shown only here — no SMS is
            sent.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Shop: <strong>{appLockRegenResult?.shop_name}</strong>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#f5f5f5',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontFamily: 'monospace', letterSpacing: '0.4em', flexGrow: 1 }}
            >
              {appLockRegenResult?.pin}
            </Typography>
            <Button
              onClick={async () => {
                await navigator.clipboard.writeText(appLockRegenResult.pin)
                setSnackbar({ type: 'success', message: 'PIN copied' })
              }}
              startIcon={<ContentCopyIcon />}
              size="small"
              variant="outlined"
            >
              Copy
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setAppLockRegenResult(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.type}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}

export default ShopsPage
